/**
 * Keyboard Shortcuts Help Modal Component - Task 29: Performance Optimization and Advanced Features
 * Modal component for displaying keyboard shortcuts help
 */

import React from 'react';

import { KeyboardShortcut } from './KeyboardShortcutsProvider';

interface KeyboardShortcutsHelpProps {
  shortcuts: KeyboardShortcut[];
  isOpen: boolean;
  onClose: () => void;
}

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({
  shortcuts,
  isOpen,
  onClose,
}) => {
  const groupedShortcuts = shortcuts.reduce(
    (acc, shortcut) => {
      if (!acc[shortcut.category]) {
        acc[shortcut.category] = [];
      }
      acc[shortcut.category].push(shortcut);
      return acc;
    },
    {} as Record<string, KeyboardShortcut[]>
  );

  const formatShortcut = (shortcut: KeyboardShortcut): string => {
    const parts = [];
    if (shortcut.ctrl) parts.push('Ctrl');
    if (shortcut.shift) parts.push('Shift');
    if (shortcut.alt) parts.push('Alt');
    if (shortcut.meta) parts.push('Cmd');
    parts.push(shortcut.key.toUpperCase());
    return parts.join(' + ');
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden'>
        <div className='flex items-center justify-between p-6 border-b'>
          <h2 className='text-xl font-semibold'>Keyboard Shortcuts</h2>
          <button
            onClick={onClose}
            className='text-gray-400 hover:text-gray-600'
          >
            ✕
          </button>
        </div>

        <div className='p-6 overflow-y-auto max-h-[60vh]'>
          {Object.entries(groupedShortcuts).map(
            ([category, categoryShortcuts]) => (
              <div key={category} className='mb-6 last:mb-0'>
                <h3 className='text-lg font-medium text-gray-900 mb-3 capitalize'>
                  {category}
                </h3>
                <div className='space-y-2'>
                  {categoryShortcuts.map((shortcut, index) => (
                    <div
                      key={index}
                      className='flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md'
                    >
                      <span className='text-gray-700'>
                        {shortcut.description}
                      </span>
                      <kbd className='px-2 py-1 bg-white border border-gray-300 rounded text-sm font-mono'>
                        {formatShortcut(shortcut)}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}

          <div className='mt-6 pt-4 border-t'>
            <p className='text-sm text-gray-600'>
              Press{' '}
              <kbd className='px-1 py-0.5 bg-gray-100 border rounded text-xs'>
                Ctrl+?
              </kbd>{' '}
              or{' '}
              <kbd className='px-1 py-0.5 bg-gray-100 border rounded text-xs'>
                Cmd+?
              </kbd>{' '}
              to show this help dialog.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
